import React from "react";

import { useRetrieveGantt, useRetrieveWbsMeta } from "@planzone/view-models";
import { GanttFilter, Sort as S } from "@planzone/dto";
import { events } from "@planzone/events";

import { usePreferences } from "@components/preferences";
import { useMessage } from "@components/useMessage";
import { createHoverStore, HoverProvider } from "@common/elementHover";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { TreeToggle } from "@common/treeToggle";
import { Wbs } from "@scenes/wbs";
import { Props } from "./types";
import { Heading } from "./heading";
import { Filters } from "./filters";
import { Sort } from "./sort";
import { ExportButton } from "./exportButton";
import styles from "./styles.scss";

function WbsPage({}: Props) {
  const [areStatusesDisplayed, setAreStatusesDisplayed] = React.useState(false);
  const [hoverStore] = React.useState(createHoverStore);
  const { state, edit } = usePreferences();
  const { wbsFilter, wbsSort: sort } = state;
  const filter: GanttFilter = {
    ...wbsFilter,
    scope: "planzone",
  };
  const setFilter = (nextFilter: GanttFilter) =>
    edit({ ...state, wbsFilter: nextFilter });
  const setSort = (nextSort: S) => edit({ ...state, wbsSort: nextSort });
  const {
    isRequestPending,
    gantt: { projects },
    currentPage,
    canRetrieveNextElements,
    retrieveGantt,
  } = useRetrieveGantt(50, sort);
  const {
    isRequestPending: isMetaRequestPending,
    isRequestSuccess: isMetaRequestSuccess,
    isRequestFailure: isMetaRequestFailure,
    meta,
    retrieveWbsMeta,
  } = useRetrieveWbsMeta(projects);

  React.useEffect(() => {
    !isMetaRequestPending &&
      !isMetaRequestSuccess &&
      !isMetaRequestFailure &&
      retrieveWbsMeta({ projects });
  }, [projects.length]);

  useMessage(
    ({ type }) =>
      (type === events.moveListElementSucceeded ||
        ["Task", "List", "Milestone", "Event"].find(
          (elementType) =>
            type === `create${elementType}Succeeded` ||
            type === `remove${elementType}Succeeded`,
        ) ||
        type === events.editAllocationSucceeded ||
        type === events.removeAllocationSucceeded ||
        type === events.allocateResourcesSucceeded) &&
      retrieveWbsMeta({ projects }),
  );

  useMessage(
    (event) =>
      (((event.type === events.saveTimeRecordSucceeded ||
        event.type === events.deleteTimeRecordSucceeded) &&
        !!meta[event.data.timeRecord.task.uuid]) ||
        (event.type === events.validateTimeRecordsUpToDateSucceeded &&
          projects.some(({ uuid }) => uuid === event.data.project.uuid))) &&
      retrieveWbsMeta({ projects }),
    { localOnly: false },
  );

  return (
    <TreeToggle>
      <div className={styles.listsPage}>
        {!areStatusesDisplayed && (
          <div className={styles.exportButton}>
            <ExportButton
              projects={projects}
              filter={filter}
              sort={sort === "zone" ? "ascendingRank" : sort}
            />
          </div>
        )}
        <div className={styles.header}>
          <div className={styles.toolbar}>
            {!areStatusesDisplayed && <Sort sort={sort} select={setSort} />}
            <Filters
              filter={filter}
              setFilter={setFilter}
              areDataTypesDisplayed={!areStatusesDisplayed}
            />
            {/*<DisplayToggle*/}
            {/*  isDisplayingStatuses={areStatusesDisplayed}*/}
            {/*  toggle={() => setAreStatusesDisplayed(!areStatusesDisplayed)}*/}
            {/*/>*/}
          </div>
        </div>
        <div className={styles.heading}>
          <Heading />
        </div>
        <div className={styles.main}>
          {!areStatusesDisplayed ? (
            <InfiniteScroll id="wbs" className={styles.wbs} canFetchOnIdle>
              {() => (
                <>
                  <HoverProvider value={hoverStore}>
                    <Wbs
                      projects={projects}
                      gantt={[]}
                      filter={filter}
                      sort={sort === "zone" ? "ascendingRank" : sort}
                      meta={meta}
                      loaderId="wbs"
                      isFetchingProjects={isRequestPending}
                    />
                  </HoverProvider>
                  <Flag
                    name="projects"
                    isDisabled={isRequestPending || !canRetrieveNextElements}
                    retrieveItems={() =>
                      retrieveGantt({ size: 50, page: currentPage, sort })
                    }
                  />
                </>
              )}
            </InfiniteScroll>
          ) : (
            // <div className={styles.statuses}>
            //   <Statuses filter={filter} />
            // </div>
            <></>
          )}
        </div>
      </div>
    </TreeToggle>
  );
}

export { WbsPage };
